import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Inbox, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  // Spec utilisateur fork 25.14 — voir TicketDetail.jsx pour le détail.
  open: { label: "En attente", cls: "bg-brand/10 text-brand" },
  pending: { label: "Ouverte", cls: "bg-[#ECFBEF] text-[#0A7F2E]" },
  closed: { label: "Clôturée", cls: "bg-destructive/10 text-destructive" },
};

export default function MyRequests() {
  const [tickets, setTickets] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const nav = useNavigate();
  const { role, user, loading: authLoading } = useAuth();

  // Guard: only accounts with a real identity (establishment, admin or a
  // non-guest visitor) get access to the in-app tracking page. Anonymous
  // guests submit support requests and follow them up via email only.
  useEffect(() => {
    if (authLoading) return;
    const allowed = role === "commerce" || (role === "client" && !user?.isGuest);
    if (!allowed) {
      nav("/", { replace: true });
    }
  }, [authLoading, role, user, nav]);

  const load = async () => {
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(data.items || []);
    } catch (err) {
      if (err.response?.status === 401) nav("/login");
      setTickets([]);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    const allowed = role === "commerce" || (role === "client" && !user?.isGuest);
    if (!allowed) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, role, user, nav]);

  // Spec utilisateur fork 25.15 — permettre la suppression des demandes
  // CLÔTURÉES uniquement, avec double-clic de confirmation pour éviter
  // les suppressions accidentelles.
  const handleDelete = async (e, ticketId) => {
    e.preventDefault();
    e.stopPropagation();
    if (confirmId !== ticketId) {
      setConfirmId(ticketId);
      setTimeout(() => setConfirmId((id) => (id === ticketId ? null : id)), 4000);
      return;
    }
    setDeletingId(ticketId);
    try {
      await api.delete(`/support/tickets/${ticketId}`);
      toast.success("Demande supprimée");
      setConfirmId(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setDeletingId(null);
    }
  };

  if (tickets === null) return <div className="container mx-auto py-16 text-center text-subtle">Chargement…</div>;

  return (
    <div className="container mx-auto px-4 md:px-8 py-10 md:py-14 max-w-4xl">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="overline mb-2">Mes demandes</p>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-ink">Support</h1>
        </div>
        <Button asChild className="bg-brand hover:bg-brand-hover text-white rounded-md h-11" data-testid="new-request-btn">
          <Link to="/contact">+ Nouvelle demande</Link>
        </Button>
      </div>

      {tickets.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center">
          <Inbox className="mx-auto text-subtle" size={36} />
          <p className="mt-4 text-ink font-medium">Aucune demande pour le moment</p>
          <p className="text-subtle text-sm mt-1">Contactez le support si vous avez besoin d'aide.</p>
        </div>
      ) : (
        <div className="border border-border divide-y divide-border">
          {tickets.map((t) => {
            const st = STATUS_LABEL[t.status] || STATUS_LABEL.open;
            return (
              <Link
                to={`/my-requests/${t.id}`}
                key={t.id}
                className="flex items-center gap-4 p-5 hover:bg-surface transition-colors"
                data-testid={`ticket-row-${t.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-xs text-subtle">{t.trackingNumber}</span>
                    <span className={`inline-block px-2 py-0.5 text-xs font-bold uppercase tracking-wider ${st.cls}`}>
                      {st.label}
                    </span>
                  </div>
                  <p className="font-display text-lg font-bold text-ink mt-1 truncate">{t.subject}</p>
                  <p className="text-xs text-subtle mt-1">
                    {new Date(t.createdAt).toLocaleString("fr-FR")} · {(t.replies || []).length} réponse{(t.replies || []).length > 1 ? "s" : ""}
                  </p>
                </div>
                {t.status === "closed" && (
                  <button
                    type="button"
                    onClick={(e) => handleDelete(e, t.id)}
                    disabled={deletingId === t.id}
                    className={`shrink-0 h-9 px-3 rounded-md border text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50 ${
                      confirmId === t.id
                        ? "bg-destructive border-destructive text-white"
                        : "border-border text-subtle hover:border-destructive hover:text-destructive"
                    }`}
                    aria-label="Supprimer cette demande"
                    title={confirmId === t.id ? "Cliquez à nouveau pour confirmer" : "Supprimer cette demande clôturée"}
                    data-testid={`ticket-delete-${t.id}`}
                  >
                    <Trash2 size={14} className={confirmId === t.id ? "mr-1 inline" : "inline"} />
                    {confirmId === t.id && <span>Confirmer</span>}
                  </button>
                )}
                <ChevronRight className="text-subtle" size={18} />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
